import type { MessageKey } from '@ecom/i18n';

/**
 * Mirrors apps/api/src/modules/identity/{merchant-administration.service.ts,
 * platform-administration.service.ts} exactly — the real, only shapes these
 * endpoints return. `MERCHANT_STATUSES` names the four values the API stores
 * (`active` is what the product calls "approved"); the graph of legal
 * transitions is NOT reproduced here — `merchant-status.ts` and, beneath it,
 * `app.transition_merchant_status()` remain the sole authority.
 * `merchantActions()` below only decides which BUTTONS to offer, as a UX hint.
 */
export const MERCHANT_STATUSES = ['pending', 'active', 'suspended', 'refused'] as const;
export type MerchantStatus = (typeof MERCHANT_STATUSES)[number];

/**
 * Single source of truth for the merchant status label, reused by
 * `MerchantStatusBadge`, `MerchantsPage`'s filter, and
 * `PlatformAnalyticsPage`'s per-status KPI cards — the same real
 * values everywhere, never a second copy of the wording. Reuses `merchant.*`
 * catalogue keys already shipped for the merchant dashboard's own identical
 * status vocabulary rather than duplicating a translation of
 * "Actif"/"Suspendu"/"En attente".
 */
export const MERCHANT_STATUS_LABEL_KEY: Record<MerchantStatus, MessageKey> = {
  pending: 'merchant.payments.bucket.pending',
  active: 'merchant.products.status.active',
  suspended: 'merchant.staff.statusSuspended',
  refused: 'admin.merchants.statusRefused',
};

/** The administrator who manages a shop (its portfolio). `null` on a summary = NOBODY does: an unassigned shop. */
export interface MerchantManager {
  readonly userId: string;
  readonly email: string | null;
  readonly displayName: string | null;
}

export interface MerchantSummary {
  readonly tenantId: string;
  readonly legalName: string | null;
  readonly status: MerchantStatus;
  readonly createdAt: string;
  /** How the shop came to exist: `operator`, `self_registration` or `legacy`. */
  readonly createdChannel?: string;
  /** Lot P2: who manages the shop. Absent only in answers from a server that predates the portfolios. */
  readonly managedBy?: MerchantManager | null;
  /**
   * The owner has never signed in and never chosen a password: the one state in which the MANAGING administrator may hand over a new
   * provisional password (decision M3). A display hint for the button; the database function decides.
   */
  readonly ownerAwaitingFirstLogin?: boolean;
}

/** What the list can be narrowed by (lot P2/P3). Every filter only NARROWS what the caller may already see. */
export interface MerchantFilters {
  readonly status: MerchantStatus | '';
  /** Free text on the legal name; ignored below two characters. */
  readonly search: string;
  /** Superadmin only: `all`, `unassigned`, or one administrator's id. */
  readonly scope: string;
}

export const SCOPE_ALL = 'all';
export const SCOPE_UNASSIGNED = 'unassigned';

/**
 * `GET /api/v1/admin/merchants` returns a bounded page since the P0
 * stabilisation lot — `total` is the count for the same filter, `limit`/
 * `offset` echo the effective bounds. Mirrors
 * `merchant-administration.service.ts`'s `MerchantListPage`.
 */
export interface MerchantListPage {
  readonly merchants: readonly MerchantSummary[];
  readonly total: number;
  readonly limit: number;
  readonly offset: number;
}

export interface MerchantStatusTransition {
  readonly tenantId: string;
  readonly previousStatus: MerchantStatus;
  readonly newStatus: MerchantStatus;
}

export interface CrossTenantMembership {
  readonly id: string;
  readonly tenant_id: string;
  readonly role_key: string;
  readonly status: string;
}

/**
 * What an operator can DO with a shop in a given status - the buttons, not the rules:
 *
 *   pending   -> approve (`active`) or refuse (`refused`)
 *   active    -> suspend
 *   suspended -> reactivate (`active`)
 *   refused   -> nothing: refusal is final
 *
 * `askPassword` marks the two actions that need the operator's own password again (suspend, refuse): the API asks for it on exactly
 * those targets, and refuses the request without it.
 */
export interface MerchantAction {
  readonly target: Exclude<MerchantStatus, 'pending'>;
  readonly labelKey: MessageKey;
  readonly askPassword: boolean;
  readonly danger: boolean;
}

/**
 * `asSuperadmin` hides approve and refuse: the superadmin never validates a shop (decision D6) - it assigns the shop to an
 * administrator, who decides. A display hint like the rest of this function; the database refuses it anyway.
 */
export function merchantActions(
  status: MerchantStatus,
  asSuperadmin = false,
): readonly MerchantAction[] {
  switch (status) {
    case 'pending':
      if (asSuperadmin) return [];
      return [
        {
          target: 'active',
          labelKey: 'admin.merchants.action.approve',
          askPassword: false,
          danger: false,
        },
        {
          target: 'refused',
          labelKey: 'admin.merchants.action.refuse',
          askPassword: true,
          danger: true,
        },
      ];
    case 'active':
      return [
        {
          target: 'suspended',
          labelKey: 'admin.merchants.action.suspend',
          askPassword: true,
          danger: true,
        },
      ];
    case 'suspended':
      return [
        {
          target: 'active',
          labelKey: 'admin.merchants.action.reactivate',
          askPassword: false,
          danger: false,
        },
      ];
    case 'refused':
      return [];
  }
}
